<template>
  <Layout>
    <div class="row">
      <PageHeader class="col-11" title="User Management" />
    </div>

    <!-- Search and Create new package -->
    <div class="row mb-2">
      <div class="col-sm-4">
        <div class="search-box me-2 mb-2 d-inline-block">
          <div class="position-relative">
            <b-form-input
              v-model="filter"
              type="search"
              class="form-control"
              placeholder="Search User"
            ></b-form-input>
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </div>
      <div class="col-sm-8">
        <div class="text-sm-end">
          <button
            type="button"
            @click="showCreatePackageModal"
            class="btn btn-info btn-rounded mb-2 me-2"
          >
            <i class="mdi mdi-plus me-1"></i
            >{{ $t("menuitems.administration.list.user.list.add") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Packages Table -->
    <div class="table-responsive mb-0">
      <b-table
        class="datatables"
        :items="allUsers"
        :fields="fields"
        responsive="sm"
        :per-page="0"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
      >
        <template #cell(role_name)="row">
          <span>{{ String(row.item.role_name).toUpperCase() }}</span>
        </template>

        <template #cell(created_at)="row">
          <span
            >{{ new Date(row.item.created_at).toLocaleDateString() }}
            {{ new Date(row.item.created_at).toLocaleTimeString() }}</span
          >
        </template>

        <template #cell(is_active)="row">
          <span :class="`badge ${getStatusClass(row.item.is_active)}`">{{
            row.item.is_active ? "Active" : "Disabled"
          }}</span>
        </template>

        <template #cell(actions)="row">
          <b-button
            size="sm"
            variant="primary"
            class="m-1"
            @click="showUserModifyModal(row.item)"
            :disabled="currentUserId === row.item.id"
          >
            Modify
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            class="m-1"
            v-show="row.item.is_active == 1"
            @click="deactivateUser(row.item.id)"
            :disabled="currentUserId === row.item.id"
          >
            Deactivate
          </b-button>
          <b-button
            size="sm"
            variant="success"
            class="m-1"
            v-show="row.item.is_active == 0"
            @click="activateUser(row.item.id)"
            :disabled="currentUserId === row.item.id"
          >
            Activate
          </b-button>
        </template>
      </b-table>
    </div>
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-end mb-3">
          <ul class="pagination pagination-rounded mb-0">
            <!-- pagination -->
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
            ></b-pagination>
          </ul>
        </div>
      </div>
    </div>

    <!-- Create new user modal -->
    <b-modal
      id="create-new-user-modal"
      title="Raise Challan"
      title-class="font-18"
      no-close-on-backdrop
      hide-footer
      centered
    >
      <template #modal-header>
        <div><h5>Create New User</h5></div>
        <div>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="cancelCreateNewUserModal()"
          >
            Cancel
          </button>
        </div>
      </template>
      <form @submit.prevent="createNewUser">
        <div class="row">
          <div class="col-6">
            <div class="mb-3">
              <label>First Name</label>
              <input
                placeholder="Enter First Name"
                v-model="createUserObj.firstName"
                type="text"
                pattern="[a-zA-Z]*"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Last Name</label>
              <input
                placeholder="Enter Last Name"
                v-model="createUserObj.lastName"
                type="text"
                pattern="[a-zA-Z]*"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Email ID</label>
              <input
                placeholder="Enter Email ID"
                v-model="createUserObj.email"
                type="email"
                pattern="[^@]+@[^@]+\.[a-zA-Z]{2,6}"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Mobile Number</label>
              <input
                placeholder="Enter Mobile Number"
                v-model="createUserObj.mobile"
                type="text"
                maxlength="10"
                pattern="[6-9]{1}[0-9]{9}"
                class="form-control inp-bg"
                required=""
              />
            </div>
          </div>
          <hr />
          <div class="col-6">
            <div class="mb-3">
              <label>Login Username</label>
              <input
                placeholder="Enter Login Username"
                v-model="createUserObj.username"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Login Password</label>
              <input
                placeholder="Enter Login Password"
                v-model="createUserObj.password"
                type="password"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-12">
            <div class="mb-3">
              <label>Role</label>
              <v-select
                class="style-chooser"
                v-model="createUserObj.role"
                :options="roles"
                background-color="white"
                placeholder="Select Role"
                required
              ></v-select>
            </div>
          </div>
        </div>

        <hr />

        <!-- BUTTON -->
        <div class="row">
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Create User Account <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>

    <!-- Update user package modal -->
    <b-modal
      id="update-user-modal"
      title="Raise Challan"
      title-class="font-18"
      hide-footer
      centered
    >
      <template #modal-header>
        <div><h5>Modify User Account</h5></div>
        <div>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="$bvModal.hide('update-user-modal')"
          >
            Cancel
          </button>
        </div>
      </template>
      <form @submit.prevent="updateUser">
        <div class="row">
          <div class="col-6">
            <div class="mb-3">
              <label>First Name</label>
              <input
                placeholder="Enter First Name"
                v-model="updateUserObj.first_name"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Last Name</label>
              <input
                placeholder="Enter Last Name"
                v-model="updateUserObj.last_name"
                type="text"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Email ID</label>
              <input
                placeholder="Enter Email ID"
                v-model="updateUserObj.email"
                type="email"
                pattern="[^@]+@[^@]+\.[a-zA-Z]{2,6}"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Mobile Number</label>
              <input
                placeholder="Enter Mobile Number"
                v-model="updateUserObj.mobile"
                type="number"
                pattern="[1-9]{1}[0-9]{9}"
                title="Please enter 10 digit mobile number"
                class="form-control inp-bg"
                required
              />
            </div>
          </div>
          <hr />
          <div class="col-6">
            <div class="mb-3">
              <label>Login Username</label>
              <input
                placeholder="Enter Login Username"
                v-model="updateUserObj.username"
                type="text"
                class="form-control inp-bg"
                required
                :disabled="true"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="mb-3">
              <label>Login Password</label>
              <input
                placeholder="Enter Login Password"
                v-model="updateUserObj.password"
                type="password"
                class="form-control inp-bg"
              />
            </div>
          </div>
          <div class="col-12">
            <div class="mb-3">
              <label>Role</label>
              <v-select
                class="style-chooser"
                v-model="updateUserObj.role"
                :options="roles"
                background-color="white"
                placeholder="Select Role"
                required
              ></v-select>
            </div>
          </div>
        </div>

        <hr />

        <!-- BUTTON -->
        <div class="row">
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Save User Details <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import packageMixin from "@/mixins/administration";
import Swal from "sweetalert2";

/**
 * Dashboard Component
 */
export default {
  page: {
    title: "User Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  mixins: [packageMixin],
  data() {
    return {
      createUserObj: {},
      updateUserObj: {},
      roles: [],
      allUsers: [],
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      sortBy: "id",
      sortDesc: false,
      filterOn: [],
      filter: null,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true,
        },
        {
          key: "first_name",
          label: "First Name",
          sortable: false,
        },
        {
          key: "last_name",
          label: "Last Name",
          sortable: false,
        },
        {
          key: "mobile",
          sortable: false,
        },
        {
          key: "email",
          sortable: false,
        },
        {
          key: "role_name",
          sortable: false,
        },
        {
          key: "is_active",
          sortable: false,
        },
        {
          key: "created_at",
          sortable: false,
        },
        {
          key: "actions",
          sortable: false,
          label: "",
        },
      ],
    };
  },
  async mounted() {
    this.$store.dispatch("layout/changeLoaderValue", true);
    const roles = await this.GetRoles();
    await this.getAllUsers();
    this.roles = roles.map((e) => e.role_name).filter(e => e !== 'rpo' && e !== 'emergency');
    this.$store.dispatch("layout/changeLoaderValue", false);
  },
  methods: {
    validate() {
      var element = document.getElementById("input-field");
      element.value = element.value.replace(/[^a-zA-Z0-9@]+/, "");
    },

    showCreatePackageModal() {
      this.$bvModal.show("create-new-user-modal");
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    async getAllUsers() {
      const user = await this.GetAllUsers();
      this.allUsers = user.filter((user) => user.role_name !== "emergency");
    },

    async createNewUser() {
      try {
        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.CreateNewUser(this.createUserObj);
        this.createUserObj = {};
        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("create-new-user-modal");
        this.allUsers = await this.GetAllUsers();
        Swal.fire(
          "Account Created!",
          "User account created successfully",
          "success"
        );
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", e.response.data.message, "error");
      }
    },

    cancelCreateNewUserModal() {
      this.createUserObj = {};
      this.$bvModal.hide("create-new-user-modal");
    },

    async updateUser() {
      try {
        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.UpdateUser(this.updateUserObj);
        this.allUsers = await this.GetAllUsers();
        this.updateUserObj = {};
        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("update-user-modal");
        Swal.fire(
          "Account Updated!",
          "User account updated successfully",
          "success"
        );
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to update user", "error");
      }
    },

    showUserModifyModal(selectedUser) {
      this.updateUserObj = {
        id: selectedUser.id,
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        email: selectedUser.email,
        mobile: selectedUser.mobile,
        role: selectedUser.role_name,
        username: selectedUser.username,
      };
      this.$bvModal.show("update-user-modal");
    },

    async deactivateUser(userId) {
      const payload = {
        id: userId,
        isActive: false,
      };

      try {
        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.UpdateUser(payload);
        this.allUsers = await this.GetAllUsers();
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire(
          "Account Deactivated!",
          "User account deactivated successfully",
          "success"
        );
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to deactivate user", "error");
      }
    },

    async activateUser(userId) {
      const payload = {
        id: userId,
        isActive: true,
      };

      try {
        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.UpdateUser(payload);
        this.allUsers = await this.GetAllUsers();
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire(
          "Account Activated!",
          "User account activated successfully",
          "success"
        );
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to activate user", "error");
      }
    },

    getStatusClass(status) {
      switch (status) {
        case true:
        case 1:
          return "bg-success";

        case false:
          return "bg-danger";

        default:
          return "bg-info";
      }
    },
  },
  computed: {
    currentUserId() {
      return this.$store.getters["auth/user"].user.id;
    },
  },
  components: {
    Layout,
    PageHeader,
  },
};
</script>